/**
 * Pure form logic for the FINN-side reschedule — kept out of the component so the two things that
 * can silently send a candidate to the wrong hour are unit-testable: when the form may be submitted,
 * and what a hand-typed wall-clock time actually means.
 */

/** The two ways a recruiter names a new time. */
export type RescheduleMode = 'slot' | 'specific';

export const MAX_MESSAGE_LENGTH = 500;
/**
 * The candidate-facing email's own cap — it is a whole message now, not the one
 * extra sentence MAX_MESSAGE_LENGTH was written for. Mirrors MAX_CANDIDATE_BODY
 * on the proxy, which is the half that binds.
 */
export const MAX_BODY_LENGTH = 2500;
/** Same floor the server puts under an "Other" reason. */
const MIN_NOTE_LENGTH = 3;

/**
 * Everything the app displays is Europe/Berlin (see TZ in taActionBoardCore), so a time typed into
 * a `datetime-local` input means Berlin wall-clock — NOT the browser's zone. Reading it with
 * `new Date(value)` would be right only for a recruiter sitting in Germany and quietly an hour or
 * more out for anyone else: an interview moved to "14:30" appearing at 15:30 for the panel, with
 * every screen in the flow agreeing on the wrong answer.
 *
 * No library available here, so the offset is found by asking Intl what Berlin's clock reads at a
 * guessed instant and correcting by the difference. Twice, because the first correction can cross a
 * DST boundary and change the offset that applies.
 *
 * @param value `YYYY-MM-DDTHH:mm` from a datetime-local input
 * @returns ISO instant, or null when the value is not a complete date+time
 */
export function berlinWallClockToIso(value: string, timeZone = 'Europe/Berlin'): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(`${value || ''}`.trim());
  if (!match) return null;
  const [, y, mo, d, h, mi] = match.map(Number) as unknown as number[];
  // The same wall-clock numbers read as UTC: the starting guess, and the target the correction
  // steers towards.
  const asUtc = Date.UTC(y, mo - 1, d, h, mi);
  if (!Number.isFinite(asUtc)) return null;
  /**
   * Reject a calendar date the input never really named. `Date.UTC` silently rolls an out-of-range
   * field over (month 13 → next January, 30 Feb → 2 March, 25:70 → the next day), and the regex
   * above admits those digits — so without this a typo would schedule a real but quietly different
   * day. Compared in pure UTC, independent of the Berlin DST arithmetic below.
   */
  const probe = new Date(asUtc);
  if (
    probe.getUTCFullYear() !== y ||
    probe.getUTCMonth() !== mo - 1 ||
    probe.getUTCDate() !== d ||
    probe.getUTCHours() !== h ||
    probe.getUTCMinutes() !== mi
  ) {
    return null;
  }

  let guess = asUtc;
  for (let i = 0; i < 2; i++) {
    const offset = zoneOffsetMs(guess, timeZone);
    const next = asUtc - offset;
    if (next === guess) break;
    guess = next;
  }
  /**
   * The correction only lands on a real instant when the typed wall clock actually exists. A
   * spring-forward gap time (02:30 on the last Sunday of March, which Berlin skips) has NO instant
   * that reads back as itself, so `guess` never reaches a fixed point — and a `datetime-local` input
   * will happily hand one over. A normalised out-of-range date (month 13, day 32) fails the same
   * way. Verify convergence: at a real time, subtracting the offset again lands back on `guess`.
   * Reject rather than silently schedule a different hour than was chosen.
   */
  if (asUtc - zoneOffsetMs(guess, timeZone) !== guess) return null;
  return new Date(guess).toISOString();
}

/** How far ahead of UTC the zone is at `instantMs`, in milliseconds. */
function zoneOffsetMs(instantMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(instantMs));
  const at = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  // `hour: '2-digit'` with hour12:false renders midnight as 24 in some engines; Date.UTC rolls that
  // into the next day, which is the same instant either way.
  const local = Date.UTC(at('year'), at('month') - 1, at('day'), at('hour'), at('minute'), at('second'));
  // `local` has second precision, so the instant is floored to seconds before differencing —
  // otherwise a stray millisecond would show up as a millisecond of "offset".
  return local - Math.floor(instantMs / 1000) * 1000;
}

export interface RescheduleFormValues {
  mode: RescheduleMode;
  /** ISO start of the chosen free slot, when mode is 'slot'. */
  slotStart: string;
  /** Raw datetime-local value, when mode is 'specific'. */
  specific: string;
  reasonCode: string;
  note: string;
  /** The whole candidate-facing email, pre-filled from the Library. */
  body: string;
  language: 'de' | 'en';
  /**
   * Whether to send the candidate our reschedule email. Ticked by default; the recruiter can untick
   * it when they have already agreed the new time another way. The candidate's own calendar copy
   * still moves either way, so this governs only the composed email.
   */
  sendEmail: boolean;
}

/**
 * Whether the form can be sent. Mirrors the server's own rules rather than trusting it to reject —
 * this button emails a candidate, so a request that was always going to 400 should never leave.
 */
export function canSubmitReschedule(v: RescheduleFormValues, nowMs = Date.now()): boolean {
  if (v.reasonCode === 'other' && v.note.trim().length < MIN_NOTE_LENGTH) return false;
  if (v.body.trim().length > MAX_BODY_LENGTH) return false;
  if (v.mode === 'slot') return Boolean(v.slotStart);
  const iso = berlinWallClockToIso(v.specific);
  if (!iso) return false;
  // A time in the past is the commonest typo (last month's date, this year's time) and the server
  // refuses it — catching it here keeps the refusal next to the field that caused it.
  return Date.parse(iso) > nowMs;
}

/** The request body for `rescheduleInterviewBooking`, or null when the form is not submittable. */
export function toRescheduleBody(
  v: RescheduleFormValues,
  nowMs = Date.now()
): {
  start: string;
  reasonCode: string;
  note?: string;
  body?: string;
  language?: 'de' | 'en';
  sendEmail?: boolean;
  override?: boolean;
} | null {
  if (!canSubmitReschedule(v, nowMs)) return null;
  const start = v.mode === 'slot' ? v.slotStart : (berlinWallClockToIso(v.specific) as string);
  const note = v.note.trim();
  const body = v.body.trim();
  return {
    start,
    reasonCode: v.reasonCode,
    ...(note ? { note } : {}),
    ...(body ? { body, language: v.language } : {}),
    // Only sent when the recruiter unticked it — the proxy treats a missing field as "send", so the
    // default never needs to travel and old clients are unaffected.
    ...(v.sendEmail ? {} : { sendEmail: false }),
    // Only the typed path skips the availability check. Sending `override` on a picked slot would
    // waive the check for a time that had just passed it — pointlessly widening what can go wrong.
    ...(v.mode === 'specific' ? { override: true } : {}),
  };
}
